import { useCallback, useEffect, useState } from 'react';

import { useUpdateProjectMutation } from 'features/master/api/master.api';
import { Snackbar, SnackbarVariant } from 'shared/ui';

import useBantBusinessRule from '../hooks/useBantBusinessRule';
import BantQualificationSection from './BantQualificationSection';
import { ProjectDetail } from './project-detail.types';

interface Props {
  project: ProjectDetail;
}

const DEFAULT_FINANCIAL_WEIGHT = 0.6;
const DEFAULT_AI_WEIGHT = 0.2;

const computeFinancialWeight = (p: ProjectDetail): number =>
  Number(p.financialScoreWeight ?? p.masterProject?.financialScoreWeight ?? DEFAULT_FINANCIAL_WEIGHT);

const computeAiWeight = (p: ProjectDetail): number =>
  Number(p.aiScoreWeight ?? p.masterProject?.aiScoreWeight ?? DEFAULT_AI_WEIGHT);

const formatMinUFInput = (value: number | null): string => (value == null ? '' : String(value));

const TabParametros = ({ project }: Props) => {
  const isSubproject = project.masterProjectId !== null;
  const [minUFInput, setMinUFInput] = useState(formatMinUFInput(project.minimumPropertyUFPrice));
  const [financialWeight, setFinancialWeight] = useState(computeFinancialWeight(project));
  const [aiWeight, setAiWeight] = useState(computeAiWeight(project));
  const [isDirty, setIsDirty] = useState(false);

  const [updateProject, { isLoading: isSaving, isSuccess, isError }] = useUpdateProjectMutation();
  const bant = useBantBusinessRule(project);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    variant: SnackbarVariant;
    message: string;
  }>({ open: false, variant: 'success', message: '' });

  useEffect(() => {
    if (isSuccess) setIsDirty(false);
  }, [isSuccess]);

  useEffect(() => {
    setMinUFInput(formatMinUFInput(project.minimumPropertyUFPrice));
    setFinancialWeight(computeFinancialWeight(project));
    setAiWeight(computeAiWeight(project));
    setIsDirty(false);
  }, [project]);

  const touch = () => setIsDirty(true);
  const resetForm = () => {
    setMinUFInput(formatMinUFInput(project.minimumPropertyUFPrice));
    setFinancialWeight(computeFinancialWeight(project));
    setAiWeight(computeAiWeight(project));
    setIsDirty(false);
    bant.reset();
  };

  const handleMinUFChange = (raw: string) => {
    if (raw === '' || /^\d*(\.\d{0,2})?$/.test(raw)) {
      setMinUFInput(raw);
      touch();
    }
  };
  const betaSum = Math.round((financialWeight + aiWeight) * 100);
  const betaValid = betaSum <= 100;
  const interestNoAi = Math.round((1 - financialWeight) * 100);
  const interestWithAi = Math.round((1 - financialWeight - aiWeight) * 100);

  const anyDirty = isDirty || bant.isDirty;

  const handleSave = async () => {
    if (!betaValid) return;
    try {
      if (isDirty) {
        const minUFValue = minUFInput.trim() === '' ? undefined : Number(minUFInput);
        await updateProject({
          id: project.id,
          patch: {
            ...(isSubproject && minUFValue !== undefined ? { minimumPropertyUFPrice: minUFValue } : {}),
            financialScoreWeight: financialWeight,
            aiScoreWeight: aiWeight,
          },
        }).unwrap();
      }
      if (bant.isDirty) await bant.save();
      setSnackbar({ open: true, variant: 'success', message: 'Cambios guardados correctamente.' });
    } catch {
      setSnackbar({ open: true, variant: 'error', message: 'Error al guardar los cambios.' });
    }
  };

  const handleBantToggle = async (nextEnabled: boolean): Promise<void> => {
    try {
      await bant.toggle(nextEnabled);
      setSnackbar({
        open: true,
        variant: 'success',
        message: nextEnabled ? 'Calificación BANT activada.' : 'Calificación BANT desactivada.',
      });
    } catch {
      setSnackbar({ open: true, variant: 'error', message: 'No se pudo actualizar la calificación BANT.' });
    }
  };

  const handleSnackbarClose = useCallback(() => setSnackbar((s) => ({ ...s, open: false })), []);

  return (
    <div className="flex flex-col gap-0">
      {/* Condiciones de venta */}
      <p className="text-xs font-medium text-text-subtle uppercase tracking-wider mb-2.5 mt-1 flex items-center gap-1.5">
        Condiciones de venta (Base Global)
      </p>

      <div className="flex flex-col mb-5">
        {isSubproject && (
          <div className="flex items-center gap-2.5 py-2.5 border-b border-border-subtle">
            <span className="text-sm text-text-default">Precio mínimo (UF)</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0"
              value={minUFInput}
              onChange={(e) => handleMinUFChange(e.target.value)}
              className="w-28 text-sm bg-bg-surface-raised border border-border-subtle rounded-lg px-3 py-1.5 text-right outline-none focus:border-brand-vivid transition-colors"
            />
          </div>
        )}
        <div className="flex items-center gap-2.5 py-2.5">
          <span className="text-sm text-text-default">Pie mínimo</span>
          <span className="text-sm text-text-muted">
            {project.minimumDownPaymentRate != null ? `${project.minimumDownPaymentRate * 100}%` : '- (Default: 20%)'}
          </span>
        </div>
      </div>

      {/* Betas */}
      <div className="flex items-center gap-2.5 mb-2.5">
        <p className="text-xs font-medium text-text-subtle uppercase tracking-wider flex items-center gap-1.5">
          Betas de puntaje
        </p>
        <span className={`text-xs font-medium tabular-nums ${betaValid ? 'text-success' : 'text-error'}`}>
          Total: <strong>{betaSum}</strong>%
        </span>
      </div>

      <p className="text-xs text-text-faint mb-3">Los valores no deben superar 1.00.</p>

      {!betaValid && (
        <div className="flex items-center gap-2 mb-3 text-xs text-error bg-error-light border border-error-light rounded-lg px-3 py-2">
          Las betas superan 1.00. Corrige los valores antes de guardar.
        </div>
      )}

      <div className="bg-bg-surface-raised border border-border-subtle rounded-lg p-3 mb-5">
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <p className="text-xs text-text-subtle mb-2">Beta puntaje financiero</p>
            <div className="flex items-center gap-2">
              <span className="text-base font-medium text-text-default tabular-nums">
                {Math.round(financialWeight * 100)}%
              </span>
              <input
                type="number"
                value={financialWeight}
                step={0.01}
                min={0}
                max={1}
                onChange={(e) => {
                  setFinancialWeight(Number(e.target.value));
                  touch();
                }}
                className="w-20 text-sm bg-bg-surface border border-border-subtle rounded-lg px-2 py-1 outline-none focus:border-brand-vivid"
              />
            </div>
          </div>
          <div>
            <p className="text-xs text-text-subtle mb-2">Beta puntaje AI</p>
            <div className="flex items-center gap-2">
              <span className="text-base font-medium text-text-default tabular-nums">
                {Math.round(aiWeight * 100)}%
              </span>
              <input
                type="number"
                value={aiWeight}
                step={0.01}
                min={0}
                max={1}
                onChange={(e) => {
                  setAiWeight(Number(e.target.value));
                  touch();
                }}
                className="w-20 text-sm bg-bg-surface border border-border-subtle rounded-lg px-2 py-1 outline-none focus:border-brand-vivid"
              />
            </div>
          </div>
        </div>
        <div className="mt-3 border-t border-border-subtle pt-3">
          <p className="text-xs text-text-subtle mb-2">Beta puntaje interés restante</p>
          <div className="grid grid-cols-2 gap-3 text-sm text-text-subtle">
            <p>
              Sin aiScore: <span className="font-medium text-text-default tabular-nums">{interestNoAi}%</span>
            </p>
            <p>
              Con aiScore: <span className="font-medium text-text-default tabular-nums">{interestWithAi}%</span>
            </p>
          </div>
        </div>
      </div>

      {isSubproject && (
        <BantQualificationSection
          draft={bant.draft}
          isEnabled={bant.isEnabled}
          isError={bant.isError}
          isLoading={bant.isLoading}
          isToggling={bant.isToggling}
          onDraftChange={bant.updateDraft}
          onToggle={(next) => {
            void handleBantToggle(next);
          }}
        />
      )}

      <div className="flex items-center justify-end gap-3 pt-2">
        {isError && <span className="text-xs text-error">Error al guardar. Intenta de nuevo.</span>}
        {anyDirty && !isError && (
          <span className="text-xs text-error flex items-center gap-1">
            <span>●</span> Cambios sin guardar
          </span>
        )}
        <button
          type="button"
          onClick={resetForm}
          disabled={isSaving || !anyDirty}
          className={`text-sm px-3 py-1.5 border rounded-lg transition-colors cursor-pointer disabled:opacity-40 ${
            anyDirty
              ? 'border-error text-error hover:bg-error-light'
              : 'border-border-default text-text-default hover:bg-bg-surface-raised'
          }`}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => {
            void handleSave();
          }}
          disabled={isSaving || !anyDirty || !betaValid}
          className={`text-sm px-4 py-1.5 rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed ${
            anyDirty ? 'bg-brand-navy text-text-inverse hover:bg-action-brand-active' : 'bg-border-default text-white'
          }`}
        >
          {isSaving ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>

      <Snackbar
        open={snackbar.open}
        variant={snackbar.variant}
        message={snackbar.message}
        onClose={handleSnackbarClose}
      />
    </div>
  );
};

export default TabParametros;
